import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { averageTexts, NO_MEASUREMENT, LINE_METRIC_OPTIONS, METRIC_LABELS, METRIC_TYPE_IDS, METRIC_TYPES, formatInteger, formatTemperatureVariation, STATUS_TONES, STATUS_LABELS, STATUS_ORDER, statusReferences } from '../../../../domain';
import { DashboardApi } from './dashboard.api';
import { StatusBandsApi } from '../../../../components/status-bands/status-bands.api';
import { CompanySelectorPresenter, MISSING_COMPANY } from '../../../../components/company-selector/company-selector.presenter';
import { PeriodSelectorPresenter, recallPeriod } from '../../../../components/period';
import { CollaboratorsKeys } from '../collaborators/collaborators.key';
import { DASHBOARD_SHORTCUTS } from './dashboard.periods';
import { DashboardKeys } from './dashboard.key';
import { DashboardScope, MetricCardScope, OptionScope, SelectorScope, SeriesPointScope, StatusBarScope } from './dashboard.scope';
const LOG = Logger.get('Mormaii.DashboardPresenter');
// @Inject
const statusBandsApi = StatusBandsApi.INSTANCE;
/** A frequencia cardiaca no catalogo de tipos de medicao. */
const HEART_RATE_TYPE_ID = 153;
// @Inject
const dashboardApi = DashboardApi.INSTANCE;
/** A legenda das medias de leitura no painel da EMPRESA - ver `showAnalytics`. */
const PER_PERSON_PER_DAY = 'Média por pessoa e por dia';
/**
 * O painel.
 *
 * Os cinco filtros sao parametros deste place, entao mudar um NAO e uma viagem:
 * o presenter move o proprio estado e pede que o endereco seja escrito de novo.
 * Dar flip para o place onde ja se esta caminharia a arvore inteira da raiz para
 * chegar na mesma conclusao.
 *
 * De onde o estado vem e a unica diferenca entre as duas entradas.
 * `applyParameters` le da URL - um link, um reload, um Voltar - e uma acao
 * escreve direto. As duas caem em {@link moveTo}, entao existe UMA descricao do
 * que estar aqui significa, e nao uma por forma de chegar.
 */
export class DashboardPresenter extends CubePresenter {
    constructor(app) {
        super(app, new DashboardScope());
        this.parentSlot = NOOP_VOID;
        /** Onde este presenter esta. E dele que o endereco e derivado. */
        this.at = {
            period: '7d',
            from: '',
            to: '',
            metric: 'FREQUENCIA_CARDIACA'
        };
        /**
         * O seletor de empresa, que agora busca no servidor.
         *
         * Antes esta tela guardava a lista INTEIRA num campo e a desenhava toda. Hoje
         * quem guarda o que esta em tela e o proprio seletor, e o que sobra aqui e a
         * escolha - que e parametro de place, como sempre foi.
         */
        this.selector = new CompanySelectorPresenter(this.update, async (companyId) => {
            this.owner?.rememberFilterCompany(companyId);
            await this.go({ companyId });
        }, () => this.app.alert('error', MISSING_COMPANY.title, MISSING_COMPANY.text));
        /**
         * O seletor de periodo.
         *
         * Sem teto: o painel le AGREGADOS - media por metrica, contagem por faixa -, e um
         * ano de agregado e uma consulta que o servidor responde. O Relatorio de Sono tem
         * teto porque le noite a noite.
         */
        this.period = new PeriodSelectorPresenter(this.update, DASHBOARD_SHORTCUTS, async ({ from, to, shortcut }) => {
            await this.go({ period: shortcut, from, to });
        }, undefined, true);
        /**
         * As seis series da consulta em vigor, por metrica.
         *
         * O painel antigo busca as seis de uma vez porque precisa delas para as
         * mini-series dos cartoes; ter todas em maos e o que torna a troca da metrica
         * do grafico instantanea. Portado igual - buscar so a selecionada custaria
         * uma requisicao por troca E deixaria os cartoes sem linha.
         */
        this.series = new Map();
        /**
         * A distribuicao por faixa de cada indicador ja visto, para a consulta em vigor.
         *
         * As series vem as seis de uma vez; as faixas vem uma por indicador, e so quando
         * ele e escolhido. Voltar a um indicador ja visto e redesenho, nao rede.
         */
        this.levels = new Map();
        /**
         * Qual requisicao ainda pode falar.
         *
         * Incrementado a cada busca, entao uma resposta que foi ultrapassada percebe
         * isso e fica calada. Sem ele, trocar de metrica duas vezes numa conexao
         * lenta escreve o grafico da primeira sobre o da segunda.
         */
        this.pending = 0;
        /** As faixas de status de FC ja buscadas, por empresa (`defaults` para todas). */
        this.bandCache = new Map();
    }
    async applyParameters(intent, initialization, last) {
        const keys = new DashboardKeys(this.app, intent);
        // A guarda de papel, que no painel antigo era o `ProtectRouter` repetido em
        // cada rota. Antes de qualquer rede: recusada, a tela nem chega a montar.
        if (!(await keys.owner?.allowEntry('dashboard', intent))) {
            return false;
        }
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onToggleExpanded = this.action(this.onToggleExpanded);
            // As empresas alimentam o seletor e nao mudam com o filtro, entao sao
            // buscadas uma vez. Falhar aqui nao impede o painel de abrir.
            //
            // So o admin escolhe empresa, como na lista de colaboradores e como no
            // painel antigo, que nao mostra seletor algum a um admin de EMPRESA: ele
            // administra uma so, e "Todas as empresas" nao quer dizer nada para ele.
            // O `/v2/companies/active` e publico e devolve a base toda, entao sem esta
            // condicao ele veria o nome de todas as empresas do cliente.
            if (this.isCompanyChooser) {
                /*
                 * Quem administra empresas escolhe entre as DELE; o administrador
                 * geral escolhe entre todas, e ai o seletor busca no servidor.
                 */
                const minhas = keys.owner?.myCompanies ?? [];
                if (minhas.length > 0) {
                    this.selector.restrictTo(minhas);
                }
                try {
                    await this.selector.showSelected(keys.owner?.companyOnArrival(keys.companyId));
                }
                catch (caught) {
                    LOG.warn('Could not resolve the selected company', caught);
                }
            }
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        if (last) {
            this.owner?.showSection('dashboard', 'Dashboard', 'Visão geral das métricas e indicadores de saúde');
        }
        // O slot primeiro, a rede depois. Esperar as requisicoes antes de entregar
        // o scope deixa a tela anterior congelada pelo tempo da rede - e para isso
        // que existe o `loading`.
        this.parentSlot(this.scope);
        const lembrado = keys.periodInAddress ? undefined : recallPeriod(DASHBOARD_SHORTCUTS);
        // Chegada por URL. O flip escreve o endereco sozinho quando o caminho
        // inteiro roda, entao isto nao pede.
        await this.moveTo({
            // Sem empresa no endereco, a PADRAO de quem esta logado: quem
            // administra mais de uma precisa dizer qual, e o servidor recusa
            // agir sem saber.
            companyId: keys.owner?.companyOnArrival(keys.companyId),
            // Sem periodo no endereco, o ultimo escolhido no painel (B-35).
            period: (lembrado?.shortcut ?? keys.period),
            from: lembrado?.from ?? keys.from,
            to: lembrado?.to ?? keys.to,
            metric: keys.metric
        });
        return true;
    }
    /**
     * O que o endereco diz enquanto este place esta aberto.
     *
     * O framework pergunta isto a cada presenter vivo sempre que a URL e escrita,
     * entao o endereco e DERIVADO de onde a aplicacao esta, e nao montado por quem
     * navegou.
     */
    publishParameters(intent) {
        const keys = new DashboardKeys(this.app, intent);
        keys.companyId = this.companyIdToPublish;
        // O atalho primeiro: os setters das pontas o consultam para decidir se
        // precisam aparecer no endereco.
        keys.period = this.at.period;
        keys.from = this.at.from;
        keys.to = this.at.to;
        keys.metric = this.at.metric;
    }
    /** Ver `RestrictedPresenter.companyIdInAddress`: com cabecalho, quem publica e ele. */
    get companyIdToPublish() {
        return this.owner?.companyIdInAddress(this.at.companyId);
    }
    /** Uma mudanca no que este presenter mostra, que nao e uma viagem. */
    /**
     * Amplia ou volta o grafico (B-43). As referencias so existem no ampliado, e sao
     * buscadas ao ampliar - o painel normal nao paga por elas.
     */
    async onToggleExpanded() {
        this.scope.expanded = !this.scope.expanded;
        await this.showReferences(this.at);
    }
    /** As faixas de status da EMPRESA para o indicador, no fundo do grafico ampliado. */
    async showReferences(target) {
        const heartRate = target.metric === 'FREQUENCIA_CARDIACA';
        this.scope.spread = this.scope.expanded && heartRate;
        if (!this.scope.expanded || !heartRate) {
            this.scope.references = [];
            return;
        }
        const company = target.companyId ?? this.owner?.companyInScope;
        const key = String(company ?? 'defaults');
        let ranges = this.bandCache.get(key);
        if (!ranges) {
            try {
                const bands = await statusBandsApi.fetchStatusBands(company === undefined ? 'defaults' : { companyId: company });
                ranges = bands.filter((band) => band.measurementTypeId === HEART_RATE_TYPE_ID);
                this.bandCache.set(key, ranges);
            }
            catch (caught) {
                LOG.warn('Could not load the status bands', caught);
                ranges = [];
            }
        }
        if (this.scope.expanded && this.at.metric === 'FREQUENCIA_CARDIACA') {
            this.scope.references = statusReferences(ranges, 'bpm');
        }
    }
    async go(change) {
        await this.moveTo({ ...(this.requested ?? this.at), ...change });
        this.updateHistory();
        await this.showReferences(this.at);
    }
    /**
     * Mostra `target`, e torna-se ele quando os dados estao em maos.
     *
     * `at` e onde os dados estao, nao para onde o leitor vai, e essa diferenca e o
     * metodo inteiro. Tudo o que descreve o pedido muda de uma vez - os seletores,
     * a data, o esqueleto - porque o leitor clicou e merece ver registrar. O que
     * espera e o `at`, e com ele o endereco, que dele e derivado.
     */
    async moveTo(target) {
        this.requested = target;
        this.showRequest(target);
        const query = {
            startDate: target.from,
            endDate: target.to,
            companyId: target.companyId ?? null
        };
        // A METRICA nao entra na assinatura: as seis series vem juntas, entao
        // trocar o indicador nao refaz o painel - redesenha o que ja esta em maos e,
        // se ainda nao veio, busca so a distribuicao das barras daquele indicador.
        const signature = JSON.stringify(query);
        if (signature === this.lastQuery) {
            this.at = target;
            this.showSeries(target);
            await this.showLevels(target, query);
            return;
        }
        const token = ++this.pending;
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            const [analytics, series, levels] = await Promise.all([
                dashboardApi.fetchAnalytics(query),
                this.fetchEverySeries(query),
                dashboardApi.fetchStatusLevels(target.metric, query)
            ]);
            if (token !== this.pending) {
                return;
            }
            // Registrado so agora. Guardado antes da requisicao, uma falha deixaria
            // isto afirmando dados que nunca chegaram, e voltar ao mesmo filtro
            // pularia a nova tentativa.
            this.lastQuery = signature;
            this.at = target;
            this.series = series;
            this.levels = new Map([[target.metric, levels]]);
            this.showAnalytics(analytics);
            this.showSeries(target);
            this.scope.statusBars = this.buildStatusBars(levels, target.metric);
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar o painel.';
            // De volta a descrever o que esta realmente na tela: o filtro nao foi
            // aplicado, entao nada deve afirmar que foi.
            this.showRequest(this.at);
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    /**
     * As barras do indicador escolhido, com a consulta que ja esta em maos.
     *
     * Busca so o que falta - a distribuicao daquele indicador -, com o mesmo token do
     * painel: trocar de indicador e logo de periodo nao deixa a resposta velha falar.
     */
    async showLevels(target, query) {
        const cached = this.levels.get(target.metric);
        if (cached) {
            this.scope.statusBars = this.buildStatusBars(cached, target.metric);
            return;
        }
        const token = ++this.pending;
        // A distribuicao do indicador novo tambem vem da rede, e o aviso de atualizacao vale aqui.
        this.scope.loading = true;
        try {
            const levels = await dashboardApi.fetchStatusLevels(target.metric, query);
            if (token !== this.pending) {
                return;
            }
            this.levels.set(target.metric, levels);
            this.scope.statusBars = this.buildStatusBars(levels, target.metric);
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            LOG.warn('Could not load the status levels', caught);
            this.scope.statusBars = [];
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    /** O que o leitor pediu, na tela antes da resposta chegar. */
    showRequest(target) {
        this.scope.metric = target.metric;
        // O seletor calcula o intervalo e move o proprio scope.
        this.period.show(target.period, target.from, target.to);
        this.scope.period = this.period.scope;
        this.buildSelectors(target);
    }
    /**
     * As seis metricas, buscadas juntas.
     *
     * Uma que falha nao derruba as outras: ela fica de fora do mapa, e o cartao
     * dela aparece sem a linha em vez de a tela inteira virar erro. E o que o
     * painel antigo faz em `loadAllMiniCharts`.
     */
    async fetchEverySeries(query) {
        const results = await Promise.all(METRIC_TYPES.map(async (metric) => {
            try {
                return [metric, await dashboardApi.fetchTimeSeries(metric, query)];
            }
            catch (caught) {
                LOG.warn(`Could not load the ${metric} series`, caught);
                return [metric, undefined];
            }
        }));
        const byMetric = new Map();
        for (const [metric, points] of results) {
            if (points) {
                byMetric.set(metric, points);
            }
        }
        return byMetric;
    }
    showAnalytics(analytics) {
        const general = analytics.generalMetrics;
        const averages = analytics.healthMetricsAverage;
        this.scope.sleepGoal = averages?.sleepGoalAverageSeconds ?? undefined;
        this.scope.periodDescription = analytics.periodInfo.periodDescription;
        /*
         * Os OITO cartoes, na ordem do desenho que o cliente mandou em 15/09/2026.
         *
         * Eram dez ate 23/09: os oito do painel antigo mais "Empresas" e "Medicoes",
         * que o porte tinha acrescentado numa terceira fileira. O desenho (ver
         * `.claude/docs/design-do-cliente.md`) mostra uma grade de duas fileiras de
         * quatro, e para chegar nela tres coisas mudaram:
         *
         * 1. **"Empresas" subiu** para a grade principal - e o segundo cartao, ao lado
         *    de "Colaboradores". Sao as duas contagens de "quem e a base", e ficavam
         *    separadas por uma fileira inteira.
         * 2. **"Medicoes" saiu.** E volume, e nao gente: nenhuma decisao sai dali, e o
         *    desenho nao o tem.
         * 3. **"Colaboradores em atencao" saiu.** Ele nao se perde - o grafico "Indice
         *    de saude geral", logo abaixo, mostra a MESMA populacao repartida pelas
         *    quatro faixas, e a lista de Colaboradores abre ordenada por quem tem mais
         *    indicador fora da faixa. O que ele tinha de proprio era o atalho visual, e
         *    era o unico cartao que nao falava de uma metrica.
         *
         * As contagens vao COM separador de milhar - "2.473", e nao "2473". A
         * referencia escreve `{totalCollaborators || 0}` direto no JSX, sem separador;
         * um cartao de painel existe para ser lido de relance, e quatro digitos
         * corridos custam uma parada (B-08).
         */
        // Sem medicao no periodo a media vem `undefined` - ver `averageTexts`.
        const texts = averageTexts(averages);
        this.scope.cards = [
            this.buildCard('Colaboradores', formatInteger(general.totalCollaborators), 'users'),
            /*
             * "Empresas" e do administrador GERAL - o numero e o de clientes da
             * plataforma, e nao diz nada a quem administra uma ou duas. Para quem
             * administra empresa a grade abre com sete, e a fileira quebra sozinha.
             */
            ...(this.isPlatformAdmin
                ? [this.buildCard('Empresas', formatInteger(general.totalCompanies), 'company')]
                : []),
            /*
             * As medias de LEITURA dizem como foram feitas (decisao de 08/10/2026): cada
             * pessoa e cada dia pesam igual, e quem mede mais nao pesa mais. O grafico
             * ao lado pesa cada leitura, entao os dois podem diferir - em setembro a
             * temperatura deu 36,96 °C no cartao e 36,76 °C na curva. O sono fica "Média":
             * o grafico dele e por noite e bate com o cartao.
             */
            this.buildAverage('SONO', texts.SONO, 'moon'),
            this.buildAverage('FREQUENCIA_CARDIACA', texts.FREQUENCIA_CARDIACA, 'heart', PER_PERSON_PER_DAY),
            this.buildAverage('HRV', texts.HRV, 'waves', PER_PERSON_PER_DAY),
            this.buildAverage('PRESSAO', texts.PRESSAO, 'activity', PER_PERSON_PER_DAY),
            this.buildAverage('OXIGENACAO', texts.OXIGENACAO, 'droplet', PER_PERSON_PER_DAY),
            /*
             * Temperatura como DELTA, e nao absoluta - e o que torna o cartao
             * coerente com a curva, desenhada como variacao em relacao a 36 C.
             *
             * O painel antigo dizia isso no NOME da metrica ("Variação de
             * Temperatura"), o que lhe dava um segundo nome so aqui. A metrica se
             * chama "Temperatura Corporal" como em todo lugar, e o "variação" desceu
             * para a legenda do valor, que e onde a informacao pertence (B-03).
             */
            // Sobre o NORMAL de cada pessoa quando o servidor o calcula (B-45); senao, 36 °C.
            averages.temperatureDeviationAverage == null
                ? this.buildAverage('TEMPERATURA', texts.TEMPERATURA, 'thermometer', `${PER_PERSON_PER_DAY}, sobre 36 °C`)
                : this.buildAverage('TEMPERATURA', formatTemperatureVariation(averages.temperatureDeviationAverage), 'thermometer', `${PER_PERSON_PER_DAY}, sobre o normal de cada pessoa`)
        ];
    }
    buildCard(label, value, icon, hint) {
        const scope = new MetricCardScope();
        scope.identity = label;
        scope.label = label;
        scope.value = value;
        scope.icon = icon;
        scope.hint = hint;
        scope.update = this.update;
        return scope;
    }
    /**
     * Um cartao de media, com "Média" abaixo.
     *
     * O rotulo sai do `METRIC_LABELS` - um nome por metrica, o mesmo de toda tela.
     *
     * A MINI-SERIE saiu em 23/09/2026, com o desenho do cliente: os cartoes dele
     * trazem so o numero e o qualificador. Ela era uma segunda leitura no mesmo pixel -
     * "quanto" e "para onde vai" -, e a tendencia continua no grafico grande logo
     * abaixo, que e onde da para le-la de verdade. O `buildSpark` continua vivo: e o
     * painel do PACIENTE que o usa, onde o cartao e a unica serie que existe.
     */
    buildAverage(metric, value, icon, hint = 'Média') {
        // Sem medicao no periodo: o traco, e a mesma frase do grafico ao lado.
        return value === undefined
            ? this.buildCard(METRIC_LABELS[metric], '-', icon, NO_MEASUREMENT)
            : this.buildCard(METRIC_LABELS[metric], value, icon, hint);
    }
    /** O grafico grande, redesenhado a partir da metrica que esta selecionada. */
    showSeries(target) {
        this.scope.series = this.buildSeries(this.series.get(target.metric), target.metric);
    }
    /**
     * A serie do grafico grande - TODOS os pontos que o backend mandou.
     *
     * Sem agrupar. O backend responde por hora e o painel antigo plota hora a
     * hora (`formatChartData`); quem agrupa por dia la e so a mini-serie dos
     * cartoes. Agrupar aqui perderia a variacao dentro do dia, que num painel de
     * saude e justamente o que se quer ver.
     *
     * Duas metricas fogem da forma comum, e as duas seguem o painel antigo:
     *
     * 1. TEMPERATURA vira DELTA em relacao a 36 C. Isso magnifica a variacao, que
     *    num grafico faz sentido - mas foi exatamente o que o time reverteu no
     *    app por confundir (ver o CLAUDE.md do frontend-app). Vale confirmar com
     *    produto qual dos dois vale aqui.
     * 2. PRESSAO vira DUAS curvas. O backend guarda um inteiro unico
     *    (`sistolica * 1000 + diastolica`), e plotar `120074` cru desenharia uma
     *    linha que nao significa nada.
     */
    buildSeries(points, metric) {
        const pressure = metric === 'PRESSAO';
        const delta = metric === 'TEMPERATURA';
        /*
         * A temperatura sobre o NORMAL de cada pessoa (B-45), quando o servidor o manda.
         * Instante sem ninguem com normal fica de fora: misturar com "sobre 36 °C" na
         * mesma curva faria um degrau que nao aconteceu.
         */
        const personal = delta && (points ?? []).some((point) => point.deviation != null);
        const usable = personal ? (points ?? []).filter((point) => point.deviation != null) : (points ?? []);
        return usable.map((point, position) => {
            const raw = point.averageValue ?? 0;
            const scope = new SeriesPointScope();
            scope.identity = `${point.date}:${position}`;
            scope.time = timeOf(point.date);
            scope.count = point.measurementCount ?? 0;
            if (pressure) {
                scope.high = Math.floor(raw / 1000);
                scope.low = raw % 1000;
                scope.value = scope.high;
            }
            else if (personal) {
                scope.value = Math.round(point.deviation * 100) / 100;
            }
            else {
                scope.value = Math.round((delta ? raw - 36 : raw) * 100) / 100;
            }
            // A sombra da frequencia cardiaca (B-43): so desenhada no grafico ampliado.
            scope.min = point.minValue ?? undefined;
            scope.max = point.maxValue ?? undefined;
            scope.update = this.update;
            return scope;
        });
    }
    /**
     * A distribuicao por faixa, somada por nivel.
     *
     * `UNKNOWN` cai no balde do `CRITICAL` porque e o que o painel antigo faz -
     * ver a nota em `domain/index.ts`. Portado fiel; mudar e decisao de produto.
     */
    /**
     * As quatro barras da distribuicao - e cada uma leva a lista que ela conta.
     *
     * A barra representa um CONJUNTO: os colaboradores cuja ultima medicao da metrica
     * escolhida caiu naquela faixa. Clicar nela abre Colaboradores com `tipo` e
     * `faixa` ja preenchidos, que sao os dois filtros que aquela tela ja tem.
     *
     * O `UNKNOWN` - medicao sem faixa cadastrada - e somado ao `CRITICAL`, porque e assim
     * que a referencia o pinta. A lista conta igual: pedir `CRITICAL` a Colaboradores
     * leva o `UNKNOWN` junto (ver `CollaboratorsApi.fetchCollaborators`).
     */
    buildStatusBars(levels, metric) {
        const totals = new Map();
        for (const level of levels ?? []) {
            const bucket = level.statusLevel === 'UNKNOWN' ? 'CRITICAL' : level.statusLevel;
            totals.set(bucket, (totals.get(bucket) ?? 0) + (level.count ?? 0));
        }
        return STATUS_ORDER.map((level) => {
            const scope = new StatusBarScope();
            scope.identity = level;
            scope.category = STATUS_LABELS[level];
            scope.count = totals.get(level) ?? 0;
            scope.color = STATUS_TONES[level];
            scope.onSelect = this.action(this.onOpenStatusBar.bind(this, metric, level, scope));
            scope.update = this.update;
            return scope;
        });
    }
    /**
     * Abre Colaboradores filtrado pela barra clicada.
     *
     * `CollaboratorsKeys.of` parte do estado vivo da aplicacao, entao a empresa do
     * CABECALHO vai junto sem ninguem copia-la - e o mesmo caminho do "Ver
     * Colaboradores" de Empresas.
     *
     * A escolhida NESTE painel e outra historia, e precisa ser copiada: as duas telas
     * publicam empresa com nomes diferentes no endereco - `DashboardCompany` aqui,
     * `CompanyId` la -, e sao nomes diferentes de proposito, porque o mesmo numero com
     * dois donos foi o que fez a troca de empresa abrir a tela na empresa anterior (ver
     * `RestrictedPresenter.companyIdInAddress`). Sem a copia, a barra de uma empresa
     * abria a lista da base INTEIRA: o mesmo defeito que os cartoes de Adocao e
     * Engajamento tinham, e pela mesma razao.
     *
     * `companyIdToPublish` e nao `at.companyId`: com o cabecalho no comando a empresa
     * ja viaja em `ce`, e escreve-la de novo aqui devolveria aquele bug de dois donos.
     */
    async onOpenStatusBar(metric, level, bar) {
        // Barra vazia nao leva a lugar nenhum: abrir uma lista que se sabe vazia so
        // tira o leitor de onde ele estava. O guarda fica AQUI e nao na view - uma
        // acao de scope que navega para o nada esta errada venha o clique de onde vier.
        if (bar.count <= 0) {
            return;
        }
        const target = CollaboratorsKeys.of(this.app);
        target.companyId = this.companyIdToPublish;
        target.measurementTypeId = METRIC_TYPE_IDS[metric];
        target.statusLevel = level;
        // A barra conta a faixa da ultima medicao DENTRO deste periodo; a lista tem de
        // contar igual, ou os dois numeros divergem (08/10/2026).
        target.statusFrom = this.at.from;
        target.statusTo = this.at.to;
        target.page = 1;
        await target.flip();
    }
    // ========== SELETORES ==========
    /** Quem tem empresa a ESCOLHER - o admin geral, ou quem administra mais de uma. */
    get isCompanyChooser() {
        // So quando a escolha NAO esta no cabecalho: dois controles para a mesma
        // coisa acabam discordando.
        return (this.owner?.canChooseCompany ?? false) && !(this.owner?.hasHeaderChooser ?? false);
    }
    /**
     * Administrador GERAL - quem enxerga a PLATAFORMA, e nao um recorte dela.
     *
     * Separado do anterior desde que administrar mais de uma empresa passou a ser
     * possivel: as duas perguntas davam a mesma resposta e deixaram de dar. O cartao
     * "Empresas" conta as empresas do sistema, e mostra-lo a quem administra duas
     * diria quantos clientes a Mormaii tem.
     */
    get isPlatformAdmin() {
        return this.owner?.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']) ?? false;
    }
    buildSelectors(target) {
        // O titulo do banner depende do papel, como la.
        this.scope.bannerTitle = this.isCompanyChooser
            ? 'Seleção de Empresa - Média dos Colaboradores'
            : 'Dashboard de Saúde - Média dos Colaboradores';
        this.scope.companySelector = this.isCompanyChooser ? this.selector.scope : undefined;
        // UM seletor para o painel inteiro (B-37). A ordem e a do grafico de linha, que
        // era o seletor principal; os rotulos vem do dominio.
        this.scope.metricSelector = this.buildSelector('Indicador', target.metric, LINE_METRIC_OPTIONS.map((option) => ({ label: option.label, value: option.value })), (value) => ({ metric: (value ?? 'FREQUENCIA_CARDIACA') }));
    }
    buildSelector(label, current, options, target) {
        const selector = new SelectorScope();
        selector.identity = label;
        selector.label = label;
        selector.summary = options.find((option) => option.value === current)?.label ?? label;
        selector.update = this.update;
        selector.options = options.map((option) => {
            const scope = new OptionScope();
            scope.identity = `${label}:${option.value ?? ''}`;
            scope.label = option.label;
            scope.value = option.value;
            scope.current = option.value === current;
            scope.onSelect = this.action(async () => {
                await this.go(target(option.value));
            });
            scope.update = this.update;
            return scope;
        });
        return selector;
    }
}
/**
 * O instante de um ponto, em milissegundos.
 *
 * O backend manda `2026-09-05T13:00:00` SEM fuso. `new Date` de uma string assim
 * le como hora local, que e o que se quer; o cuidado e com a forma so-data
 * (`2026-09-05`), que a mesma funcao le como UTC e no Brasil volta um dia. Por
 * isso a data sem hora recebe um `T00:00:00` antes de virar `Date`.
 */
function timeOf(date) {
    if (!date) {
        return 0;
    }
    return new Date(date.includes('T') ? date : `${date}T00:00:00`).getTime();
}
/**
 * A mini-serie de um cartao: agrupada por dia, no maximo 7 pontos na semana e 30
 * no mes. E o `extractChartValues` do painel antigo.
 *
 * O agrupamento e por fatia de string, e nao por `new Date(date).toISOString()`.
 * O backend manda sem fuso, o `Date` le como local e o `toISOString` converte para
 * UTC - no Brasil isso volta um dia, e o painel antigo agrupa as leituras da
 * madrugada no dia anterior. E defeito, nao visual, entao nao foi replicado.
 */
//# sourceMappingURL=dashboard.presenter.js.map